// The athlete's clubs (see `features/athlete-clubs.md`) — the "My clubs" tab.
//
// One card per club, each answering the two questions the athlete opened the app
// for: how often am I actually training there, and what is next. Tapping a card
// opens the club page. An athlete at a single club still sees the list; it is one
// card, and it is where their attendance lives.

import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import CardActionArea from '@mui/material/CardActionArea';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import type { AthleteClubSummary } from '../../domain';
import { useTranslation } from '../../state/I18nContext';
import { UpcomingSessionRow } from './UpcomingSessionRow';

interface MyClubsListProps {
  clubs: AthleteClubSummary[];
  onOpen: (companyId: number) => void;
}

export function MyClubsList({ clubs, onOpen }: MyClubsListProps) {
  const { t, localeCode } = useTranslation();

  const dateFormat = useMemo(
    () => new Intl.DateTimeFormat(localeCode, { dateStyle: 'medium', timeZone: 'UTC' }),
    [localeCode],
  );

  if (clubs.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <ShieldOutlinedIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {t('athlete.clubs.empty')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h6">
        {t('athlete.clubs.title', { count: clubs.length })}
      </Typography>
      {clubs.map((club) => (
        <Paper key={club.companyId} variant="outlined" sx={{ overflow: 'hidden' }}>
          <CardActionArea onClick={() => onOpen(club.companyId)} sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={club.logoUrl ?? undefined}
                alt={club.name}
                variant="rounded"
                sx={{ width: 56, height: 56 }}
              >
                <ShieldOutlinedIcon />
              </Avatar>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="subtitle1" noWrap title={club.name}>
                  {club.name}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.5}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 0.5 }}
                >
                  {club.isHome && (
                    <Chip size="small" color="primary" label={t('athlete.clubs.homeClub')} />
                  )}
                  {club.locationName && (
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={<PlaceOutlinedIcon />}
                      label={club.locationName}
                    />
                  )}
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t('athlete.clubs.members', { count: club.memberCount })}
                  />
                  {club.classCount > 0 && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t('athlete.clubs.classes', { count: club.classCount })}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>

            {/* How often they actually train here. The per-week figure is the
                headline; the rate is context for it. */}
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography variant="h6" color="primary">
                  {t('athlete.clubs.perWeek', { count: club.attendance.perWeek })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('athlete.clubs.perWeekWindow', {
                    days: club.attendance.windowDays,
                  })}
                </Typography>
              </Stack>
              {club.attendance.marked > 0 ? (
                <>
                  <LinearProgress
                    variant="determinate"
                    value={club.attendance.rate * 100}
                    sx={{ mt: 1, borderRadius: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {t('athlete.clubs.attendance', {
                      present: club.attendance.present,
                      marked: club.attendance.marked,
                      percent: Math.round(club.attendance.rate * 100),
                    })}
                    {club.attendance.lastAttended &&
                      ` · ${t('athlete.clubs.lastAttended', {
                        date: dateFormat.format(
                          new Date(`${club.attendance.lastAttended}T00:00:00Z`),
                        ),
                      })}`}
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {t('athlete.clubs.noAttendance')}
                </Typography>
              )}
            </Box>

            {/* What is next here. */}
            <Box sx={{ mt: 2 }}>
              {club.nextSession ? (
                <UpcomingSessionRow session={club.nextSession} highlight />
              ) : (
                <Stack direction="row" spacing={1} alignItems="center">
                  <EventAvailableIcon color="disabled" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    {t('athlete.clubs.noUpcoming')}
                  </Typography>
                </Stack>
              )}
            </Box>
          </CardActionArea>
        </Paper>
      ))}
    </Stack>
  );
}
